'use client';

import { useEffect, useMemo, useState } from 'react';
import { CloudLightning, CloudRain, Church, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getDiasSS } from '@/lib/actions';
import {
  detallesDelDia, esDiaSiguiente, horaDeSalida, ordenarHorasDesde,
} from '@/lib/programa-ss';
import {
  WEATHER_META, madridDateStr, slotDeHoraTexto,
  type CeldaTiempo,
} from '@/lib/tiempo';
import { cn } from '@/lib/utils';
import type { DiaSS, ItinerarioPunto } from '@/lib/types';

/**
 * El día siguiente de una fecha `YYYY-MM-DD`, en aritmética de calendario pura
 * (`Date.UTC`): sumar 86.400.000 ms a una fecha LOCAL se equivoca en los dos
 * cambios de hora del año, y aquí la clave tiene que casar exacta con la del
 * pronóstico.
 */
function masUnDia(fecha: string): string {
  const [y, m, d] = fecha.split('-').map(Number);
  const t = new Date(Date.UTC(y, (m || 1) - 1, d || 1) + 86400000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())}`;
}

/** `dia.fecha` llega como ISO (viene saneado de la CF) o como Timestamp. */
function fechaDelDia(dia: DiaSS): string | null {
  const f: any = dia.fecha;
  if (!f) return null;
  const d = typeof f?.toDate === 'function' ? f.toDate() : new Date(f);
  return isNaN(d.getTime()) ? null : madridDateStr(d);
}

const TIPO_ETIQUETA: Partial<Record<ItinerarioPunto['tipo'], string>> = {
  salida: 'Salida',
  carreraOficial: 'Carrera Oficial',
  catedral: 'Catedral',
  entrada: 'Entrada',
};

/** Una celda del cuadro de horarios, ya resuelta contra el pronóstico. */
interface Celda {
  hora: string;
  calle: string;
  tipo: ItinerarioPunto['tipo'];
  /** Fecha del pronóstico a la que pertenece (la madrugada cae en el día siguiente). */
  fecha: string;
  tiempo: CeldaTiempo;
  /** `false` si esa fecha no está en el pronóstico de 14 días. */
  conocido: boolean;
}

interface Afectada {
  clave: string;
  hermandadId: string;
  nombre: string;
  escudoUrl?: string;
  color?: string;
  diaId: string;
  diaNombre: string;
  fecha: string;
  celdas: Celda[];
  lluvia: number;
  tormenta: number;
  /** Probabilidad media de las celdas en riesgo (0-100), para ordenar y mostrar. */
  probMedia: number;
  /** La peor probabilidad de todas: el «como poco, tanto». */
  probMax: number;
}

/**
 * A QUIÉN LE PILLA EL AGUA: cruza el cuadro de horarios de cada cofradía del
 * programa con el pronóstico que se está editando al lado.
 *
 * <p>Se calcula sobre el `forecast` que tiene la página EN MEMORIA, no sobre el
 * guardado: así, al cambiar una casilla en la rejilla, la lista se recoloca al
 * momento y se ve a quién se le acaba de meter (o quitar) la lluvia antes de
 * pulsar Guardar.</p>
 *
 * <p><b>Habla de RIESGO, no de certezas</b>: desde que las casillas llevan
 * probabilidad, lo que se cuenta son las celdas del cuadro que caen en horas con
 * lluvia o tormenta PROGRAMADA, y de cada una se enseña su porcentaje. No se
 * resuelve el dado aquí a propósito — el admin está decidiendo el riesgo, y ver
 * «no le va a llover» sobre un 80% sería engañoso.</p>
 *
 * <p><b>La madrugada cuenta como el día siguiente</b>, con la misma regla que el
 * libro (`esDiaSiguiente`, anclada a la hora de salida de esa cofradía): una que
 * se recoge a las 02:30 se moja con el pronóstico del día de después, no con el
 * de su fecha. Sin eso, a la Madrugá se le miraría el tiempo equivocado entero.</p>
 */
export function TiempoAfectadas({
  forecast, dias: diasVisibles,
}: {
  /** El pronóstico en edición, ya en casillas de 15 min. */
  forecast: Record<string, CeldaTiempo[]>;
  /** Las 14 fechas que se están editando (`YYYY-MM-DD`). */
  dias: string[];
}) {
  const { jartinetData } = useAuth();
  const [diasSS, setDiasSS] = useState<DiaSS[] | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    getDiasSS()
      .then((r) => { if (!cancelado) setDiasSS(r.dias || []); })
      .catch(() => { if (!cancelado) setDiasSS([]); });
    return () => { cancelado = true; };
  }, []);

  const hermandades = jartinetData?.hermandades || [];

  const { afectadas, diasEnVentana, sinCuadro } = useMemo(() => {
    const ventana = new Set(diasVisibles);
    const out: Afectada[] = [];
    let enVentana = 0;
    let sin = 0;

    for (const dia of diasSS || []) {
      const fecha = fechaDelDia(dia);
      if (!fecha || !ventana.has(fecha)) continue;
      enVentana++;

      for (const det of detallesDelDia(dia)) {
        const itinerario = det.itinerario || {};
        const horas = Object.keys(itinerario);
        if (horas.length === 0) { sin++; continue; }

        const salida = horaDeSalida(itinerario);
        const celdas: Celda[] = ordenarHorasDesde(horas, salida).map((hora) => {
          const p = itinerario[hora] || ({} as ItinerarioPunto);
          const f = esDiaSiguiente(hora, salida) ? masUnDia(fecha) : fecha;
          const dd = forecast[f];
          return {
            hora,
            calle: p.calle || '',
            tipo: p.tipo || 'itinerario',
            fecha: f,
            tiempo: (dd && dd[slotDeHoraTexto(hora)]) || { w: 'clear', p: 0 },
            conocido: Array.isArray(dd),
          };
        });

        const riesgo = celdas.filter((c) => c.conocido && c.tiempo.w !== 'clear' && c.tiempo.p > 0);
        if (riesgo.length === 0) continue;

        const lluvia = riesgo.filter((c) => c.tiempo.w === 'rain').length;
        const tormenta = riesgo.filter((c) => c.tiempo.w === 'thunder').length;
        const probMedia = Math.round(riesgo.reduce((s, c) => s + c.tiempo.p, 0) / riesgo.length);
        const probMax = riesgo.reduce((m, c) => Math.max(m, c.tiempo.p), 0);

        const h = hermandades.find((x: any) => x.id === det.hermandadId);
        out.push({
          clave: `${dia.id}#${det.hermandadId}`,
          hermandadId: det.hermandadId,
          nombre: h?.nombre || h?.nombreCompleto || det.hermandadId,
          escudoUrl: h?.escudoUrl,
          color: h?.colorPrimario,
          diaId: dia.id,
          diaNombre: dia.nombredia || '',
          fecha,
          celdas,
          lluvia,
          tormenta,
          probMedia,
          probMax,
        });
      }
    }

    // Las que peor lo llevan primero: pesan las celdas, la probabilidad media y
    // que sea tormenta (que es lo gordo).
    const riesgoDe = (a: Afectada) => (a.tormenta * 2 + a.lluvia) * (a.probMedia / 100);
    out.sort((a, b) => riesgoDe(b) - riesgoDe(a)
      || a.fecha.localeCompare(b.fecha)
      || a.nombre.localeCompare(b.nombre, 'es'));
    return { afectadas: out, diasEnVentana: enVentana, sinCuadro: sin };
  }, [diasSS, forecast, diasVisibles, hermandades]);

  const totalCeldas = afectadas.reduce((n, a) => n + a.lluvia + a.tormenta, 0);

  return (
    <div className="space-y-3 rounded-2xl border border-white/8 bg-card/40 p-4">
      <div>
        <h2 className="flex items-center gap-2 font-headline text-lg font-bold">
          <CloudRain className="h-4 w-4 text-sky-400" /> A quién le pilla el agua
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Cofradías cuyo <strong>cuadro de horarios</strong> cae en casillas con lluvia o tormenta
          programada, con la <strong>probabilidad</strong> de cada una. Se recalcula al tocar la
          rejilla, <strong>antes</strong> de guardar.
        </p>
      </div>

      {diasSS === null ? (
        <p className="flex items-center gap-2 py-6 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Leyendo el programa…
        </p>
      ) : diasEnVentana === 0 ? (
        <p className="rounded-xl bg-muted/30 p-3 text-xs text-muted-foreground">
          Ningún día del Programa de Semana Santa cae dentro de estos 14 días, así que no hay nada que
          cruzar. (El pronóstico solo llega a 14 días vista.)
        </p>
      ) : afectadas.length === 0 ? (
        <p className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-3 text-xs text-emerald-200">
          Con este pronóstico <strong>no hay riesgo para nadie</strong>: los {diasEnVentana} día
          {diasEnVentana === 1 ? '' : 's'} del programa que caen en la ventana están despejados a las
          horas de sus cuadros.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 text-[0.7rem] text-muted-foreground">
            <span><strong className="text-foreground">{afectadas.length}</strong> cofradía{afectadas.length === 1 ? '' : 's'}</span>
            <span><strong className="text-foreground">{totalCeldas}</strong> celda{totalCeldas === 1 ? '' : 's'} en riesgo</span>
          </div>

          <div className="flex max-h-[70vh] flex-col gap-1.5 overflow-y-auto pr-1">
            {afectadas.map((a) => {
              const abierto = abierta === a.clave;
              return (
                <div key={a.clave} className="rounded-xl border border-white/8 bg-muted/20">
                  <button
                    type="button"
                    onClick={() => setAbierta((x) => (x === a.clave ? null : a.clave))}
                    className="flex w-full items-center gap-2 p-2.5 text-left transition-colors hover:bg-white/[0.04]"
                  >
                    {abierto ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full border-2"
                      style={{ background: '#0a0a0c', borderColor: a.color || '#3f3f46' }}
                    >
                      {a.escudoUrl
                        ? <img src={a.escudoUrl} alt="" className="h-5 w-5 object-contain" />
                        : <Church className="h-4 w-4 text-white/50" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{a.nombre}</span>
                      <span className="block truncate text-[0.65rem] capitalize text-muted-foreground">
                        {a.diaNombre} · {a.celdas.length} celdas · hasta un{' '}
                        <span className="font-bold not-italic text-sky-300">{a.probMax}%</span>
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-0.5">
                      {a.lluvia > 0 && (
                        <span className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.6rem] font-bold"
                          style={{ background: `${WEATHER_META.rain.color}22`, color: WEATHER_META.rain.color }}>
                          <CloudRain className="h-3 w-3" /> {a.lluvia}
                        </span>
                      )}
                      {a.tormenta > 0 && (
                        <span className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.6rem] font-bold"
                          style={{ background: `${WEATHER_META.thunder.color}22`, color: WEATHER_META.thunder.color }}>
                          <CloudLightning className="h-3 w-3" /> {a.tormenta}
                        </span>
                      )}
                      <span className="text-[0.55rem] text-muted-foreground">{a.probMedia}% medio</span>
                    </span>
                  </button>

                  {abierto && (
                    <div className="border-t border-white/8 p-2">
                      <p className="mb-1.5 px-1 text-[0.6rem] text-muted-foreground">
                        Cuadro completo. Las celdas resaltadas son las que le pillan con agua
                        programada, con su probabilidad.
                      </p>
                      <div className="flex max-h-72 flex-col gap-px overflow-y-auto pr-1">
                        {a.celdas.map((c) => {
                          const mal = c.conocido && c.tiempo.w !== 'clear' && c.tiempo.p > 0;
                          const meta = WEATHER_META[c.tiempo.w];
                          const Icono = meta.icon;
                          return (
                            <div
                              key={`${c.hora}-${c.calle}`}
                              className={cn(
                                'flex items-baseline gap-2 rounded-md px-1.5 py-1 text-[0.68rem]',
                                mal ? 'font-semibold' : 'odd:bg-white/[0.02]',
                              )}
                              style={mal
                                // La opacidad del fondo va con la probabilidad, igual que en la
                                // rejilla: se ve de un vistazo cuál es la mala de verdad.
                                ? {
                                  background: `${meta.color}${Math.round(0.10 * 255 + 0.28 * 255 * (c.tiempo.p / 100)).toString(16).padStart(2, '0')}`,
                                  boxShadow: `inset 2px 0 0 ${meta.color}`,
                                }
                                : undefined}
                            >
                              <Icono
                                className="h-3 w-3 shrink-0 self-center"
                                style={{ color: mal ? meta.color : 'rgba(255,255,255,.25)' }}
                              />
                              <span className="w-10 shrink-0 font-mono" style={{ color: mal ? meta.color : undefined }}>
                                {c.hora}
                              </span>
                              <span className="min-w-0 flex-1 leading-tight">
                                {c.calle}
                                {TIPO_ETIQUETA[c.tipo] && (
                                  <span className="ml-1 text-[0.55rem] uppercase tracking-wide text-muted-foreground">
                                    · {TIPO_ETIQUETA[c.tipo]}
                                  </span>
                                )}
                              </span>
                              {mal && (
                                <span className="shrink-0 tabular-nums" style={{ color: meta.color }}>
                                  {c.tiempo.p}%
                                </span>
                              )}
                              {c.fecha !== a.fecha && (
                                <span className="shrink-0 text-[0.55rem] text-muted-foreground" title="Madrugada: cuenta como el día siguiente">
                                  +1d
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {sinCuadro > 0 && (
        <p className="text-[0.65rem] text-muted-foreground">
          {sinCuadro} cofradía{sinCuadro === 1 ? '' : 's'} de esos días todavía no {sinCuadro === 1 ? 'tiene' : 'tienen'} cuadro
          de horarios, así que no {sinCuadro === 1 ? 'entra' : 'entran'} en la cuenta.
        </p>
      )}
    </div>
  );
}
